import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import Wordmark from './Wordmark';

// Blog and Tech explained are WordPress (public_html/blog), not part of this
// React app, so they need a full page load instead of client-side routing
export const NAV_ITEMS = [
  { name: 'Home', path: '/' },
  { name: 'Blog', path: '/blog/', external: true },
  { name: 'Dictionary', path: '/dictionary', alsoActiveOn: '/define/' },
  { name: 'Word of the day', path: '/word-of-the-day' },
  { name: 'Tech explained', path: '/blog/tech-explained/', external: true },
  { name: 'Translation', path: '/translations' },
];

const NavLink = ({ item, active, className, onClick }) => {
  if (item.external) {
    return <a href={item.path} className={className} onClick={onClick}>{item.name}</a>;
  }
  return (
    <Link
      to={item.path}
      className={className}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
    >
      {item.name}
    </Link>
  );
};

const Header = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Show the header's bottom edge only once content is scrolling under it
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const isActive = (item) => {
    if (item.external) return false;
    if (item.path === '/') return pathname === '/';
    return pathname.startsWith(item.path) || (item.alsoActiveOn && pathname.startsWith(item.alsoActiveOn));
  };

  return (
    <header className={`site-header sticky top-0 z-40 ${scrolled || menuOpen ? 'is-scrolled' : ''}`}>
      <div className="wrap flex h-16 items-center justify-between gap-6">
        <Link to="/" className="rounded-sm text-xl" aria-label="Definitionofthe.com home" data-testid="site-logo">
          <Wordmark />
        </Link>

        <nav aria-label="Main" className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <li key={item.name}>
                <NavLink
                  item={item}
                  active={isActive(item)}
                  className={`block rounded-full px-3.5 py-2 text-[0.95rem] font-medium transition-colors duration-200 ${
                    isActive(item)
                      ? 'bg-ink text-paper'
                      : 'text-ink-soft hover:bg-ink/[0.06] hover:text-ink'
                  }`}
                />
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggleDarkMode}
            className="grid h-10 w-10 place-items-center rounded-full text-ink-soft transition-colors hover:bg-ink/[0.06] hover:text-ink"
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            data-testid="dark-mode-toggle"
          >
            {darkMode ? <Sun size={20} aria-hidden="true" /> : <Moon size={20} aria-hidden="true" />}
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="grid h-10 w-10 place-items-center rounded-full text-ink transition-colors hover:bg-ink/[0.06] lg:hidden"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
          >
            {menuOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav id="mobile-menu" aria-label="Main" className="sheet-in border-t border-rule lg:hidden">
          <ul className="wrap py-3">
            {NAV_ITEMS.map((item) => (
              <li key={item.name}>
                <NavLink
                  item={item}
                  active={isActive(item)}
                  onClick={() => setMenuOpen(false)}
                  className={`block rounded-2xl px-4 py-3 font-display text-xl transition-colors ${
                    isActive(item) ? 'bg-ink text-paper' : 'text-ink-soft hover:bg-ink/[0.06] hover:text-ink'
                  }`}
                />
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
};

export default Header;
